import { Locales } from '@monoimin/sb-types';

import { isValidDate } from '../helpers';
/**
 * Returns a function that formats a date according to a specified format and locale.
 * @param locale - The locale to use for formatting.
 * @returns A function that takes a date and a format string and returns a formatted date string.
 * @example const customFormat = getCustomFormat('ru'); customFormat(new Date(), 'DD MMMM'); // Returns '26 июля'
 */
type IntlDayFormat = 'numeric' | '2-digit';
export function getCustomFormat(locale: Locales) {
  /**
   * Formats a date according to a specified format.
   * @param date - The date to format.
   * @param format - The format string.
   * @returns A formatted date string.
   */
  return function customDateFormat(
    date: Date | undefined | string,
    format = 'DD MMMM, YYYY',
    timezone?: string,
    logAttributes?: object
  ): string {
    // If the date is undefined, return an empty string.
    if (!date || !isValidDate(date)) {
      return '';
    }

    try {
      if (typeof date === 'string') {
        date = new Date(Date.parse(date));
      }

      // If a timezone is provided, create a new Date object that represents the current date and time in the specified timezone.
      if (timezone) {
        date = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
      }
      // TODO при возможности использовать нативные функции padStart padEnd
      // Helper function to pad a number with a leading zero if it's less than 10.
      const pad = (n: number): string => (n < 10 ? '0' + n : n.toString());

      // Create a regular expression pattern that matches 'D' or 'DD' followed by any character (except for line terminators) and then 'MMM' or 'MMMM'.
      const pattern = /(D{1,2}.*(?=(MMM{1,4})))/;

      // Check if the format string matches the pattern.
      const isDayBeforeMonth = pattern.test(format);
      const additionalMonthParams = isDayBeforeMonth
        ? { day: 'numeric' as IntlDayFormat }
        : {};
      // Get the full name of the month depending day token position to get correct month names.
      const MMMM = date.toLocaleString(locale, {
        month: 'long',
        ...additionalMonthParams,
      });
      // Get names of weekday.
      const WWWW = date.toLocaleString(locale, {
        weekday: 'long',
      });
      const WWW = date.toLocaleString(locale, {
        weekday: 'short',
      });
      const WW = date.toLocaleString(locale, {
        weekday: 'narrow',
      });
      // Get short name of the month.
      const MMM = date.toLocaleString(locale, {
        month: 'short',
        ...additionalMonthParams,
      });

      // Define the format tokens and their corresponding date values.
      const formatTokens: { [key: string]: string } = {
        DD: pad(date.getDate()).replace('.', ''), //some languages have dots in day names so we remove them
        D: date.getDate().toString().replace('.', ''), //in some languages day names have dots so we remove them
        YYYY: date.getFullYear().toString(),
        YY: date.getFullYear().toString().slice(-2),
        HH: pad(date.getHours()),
        H: date.getHours().toString(),
        hh: pad(date.getHours() % 12 || 12),
        h: (date.getHours() % 12 || 12).toString(),
        mm: pad(date.getMinutes()),
        m: date.getMinutes().toString(),
        SS: pad(date.getSeconds()),
        S: date.getSeconds().toString(),
        A: date.getHours() < 12 ? 'AM' : 'PM',
        a: date.getHours() < 12 ? 'am' : 'pm',
        MMMM: MMMM.replace(/[\s0-9]/g, '').replace('.', ''), //in some languages month names have dots so we remove them
        MMM: MMM.replace(/[\s0-9]/g, ''),
        MM: pad(date.getMonth() + 1),
        M: String(date.getMonth() + 1),
        WWWW,
        WWW,
        WW,
      };

      // Initialize the formatted date as the format string.
      let formattedDate = format;

      // Replace each format token in the format string with its corresponding date value.
      for (const token in formatTokens) {
        const regex = new RegExp(
          `(?<![a-zA-Z0-9])${token}(?![a-zA-Z0-9])`,
          'g'
        );

        formattedDate = formattedDate.replace(regex, formatTokens[token]);
      }

      // Return the formatted date.
      return formattedDate;
    } catch (error: any) {
      fetch('/api/log/', {
        method: 'POST',
        body: JSON.stringify({
          attributes: {
            ...logAttributes,
            date: String(date),
            errorMessage: error.message,
          },
          message: 'Date format error',
        }),
      });

      if (date instanceof Date) {
        date = timezone
          ? new Date(date.toLocaleString('en-US', { timeZone: timezone }))
          : date;
        return new Intl.DateTimeFormat('en-CA', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).format(date);
      }
      return date ? date.toString() : '';
    }
  };
}

export default getCustomFormat;

// Define the test cases
// eslint-disable-next-line
/**
 * Array of test date format patterns used for validation and testing.
 * Each pattern represents a different way to format dates:
 * - DD: Day of month (01-31)
 * - D: Day of month (1-31)
 * - MMMM: Full month name
 * - MMM: Short month name
 * - YYYY: Full year
 * - YY: Short year
 * - WWWW: Full weekday name
 * - WWW: Short weekday name
 * - WW: Single letter weekday
 * - HH: Hours in 24-hour format (00-23)
 * - H: Hours in 24-hour format (0-23)
 * - mm: Minutes (00-59)
 * - m: Minutes (0-59)
 * - ss: Seconds (00-59)
 * - s: Seconds (0-59)
 *
 * @example
 * ```ts
 * const formats = [
 *   'DD MMMM YYYY',    // 26 March 2024
 *   'D MMM YY',        // 26 Mar 24
 *   'WWWW, D MMMM',    // Tuesday, 26 March
 *   'HH:mm:ss',        // 15:30:45
 *   'DD/MM/YYYY HH:mm' // 26/03/2024 15:30
 * ];
 * ```
 */
